import { memo } from "react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Flag, Zap, Volume2, VolumeX, Sun, Moon, Pause, Play, ScrollText, Layers } from "lucide-react";

import { useCountdown } from "@/hooks/useCountdown";
import MobileMenu from "@/components/MobileMenu";

/**
 * Top header bar for the active game screen.
 *
 * Pure presentational — all navigation/state handlers come in as props.
 *
 * `onSurrender` (Phase 2) is optional. When provided, a small flag button
 * renders to the right of the round/melee badges. The parent is responsible
 * for the confirm prompt + dispatching the surrender action; this component
 * just wires the click.
 */
function GameHeader({
  game,
  phase,
  currentPlayerName,
  isDaily,
  muted,
  setMuted,
  showEventLog,
  setShowEventLog,
  showDecks,
  setShowDecks,
  onBack,
  onSurrender,
  onTogglePause,
  canPause,
}) {
  const { t } = useTranslation();
  // KNOWN_ISSUES §1.45 — `next-themes` toggle. `resolvedTheme` is
  // "light" | "dark" after system preference is resolved; clicking
  // flips to the opposite explicit value (escapes "system" follow
  // on first click, which is what users expect from a manual toggle).
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  return (
    <header
      className="flex items-center justify-between px-2 sm:px-4 py-2 bg-white/60 dark:bg-slate-900/60 backdrop-blur border-b border-[#1A237E]/10 dark:border-[#E3F2FD]/10"
      data-testid="game-header"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn" className="p-1 sm:p-2" aria-label={t("gameHeader.label.back")}>
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        </Button>
        <h1
          className="text-base sm:text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] hidden sm:block"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("gameHeader.appName")}
        </h1>
        {isDaily && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{t("gameHeader.badge.daily")}</Badge>}
        {/* Limited-time mode chip. Renders one badge per active
            modifier so the player knows the rules are spicy. The
            game-state response carries the modifier id list. */}
        {(game.modifiers || []).map((m) => (
          <Badge
            key={m}
            className="bg-[#FF5A5F]/10 text-[#FF5A5F] border border-[#FF5A5F]/30 text-[10px]"
            data-testid={`modifier-badge-${m}`}
            title={t(`liveMode.modifier.${m}`, { defaultValue: m })}
          >
            <Zap className="w-3 h-3 mr-0.5" />
            {t(`liveMode.modifier.${m}`, { defaultValue: m })}
          </Badge>
        ))}
      </div>
      <div className="flex items-center gap-1 sm:gap-4 text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD] min-w-0">
        <Badge
          variant="outline"
          className="border-[#38B6FF] text-[#38B6FF] text-[10px] sm:text-xs"
          data-testid="round-badge"
        >
          {t("gameHeader.badge.round", { round: game.round_number })}
        </Badge>
        <Badge
          variant="outline"
          className="border-[#FFC107] text-[#FFC107] text-[10px] sm:text-xs"
          data-testid="melee-badge"
        >
          <Zap className="w-3 h-3 mr-0.5" /> {game.market_melee_count}/3
        </Badge>
        <span
          className="phase-badge px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs text-white bg-[#38B6FF]"
          data-testid="phase-badge"
        >
          {phase === "decision"
            ? t("gameHeader.phase.decision")
            : phase === "action_event"
            ? t("gameHeader.phase.action")
            : phase === "discard"
            ? t("gameHeader.phase.discard")
            : phase === "reveal"
            ? t("gameHeader.phase.reveal")
            : phase}
        </span>
        <TurnClockChip turnExpiresAt={game.turn_expires_at} paused={game.paused} />
      </div>
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        {/* Player name is redundant with LiveScoreboard below on
            mobile (where every pixel matters); hide until ≥sm. */}
        <span
          className="hidden sm:inline text-xs sm:text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD] truncate max-w-[8rem]"
          title={currentPlayerName}
        >
          {currentPlayerName}
        </span>
        {/* Event-log + Decks drawer toggles, surfaced as visible
            icon buttons on mobile/tablet so the panels are
            discoverable without opening the hamburger. lg:hidden —
            both panels render inline at the wide breakpoint and
            don't need a toggle. */}
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden p-1.5"
          onClick={() => setShowEventLog(!showEventLog)}
          data-testid="toggle-log-icon"
          aria-expanded={showEventLog}
          aria-pressed={showEventLog}
          aria-label={showEventLog ? t("gameHeader.label.hideLog") : t("gameHeader.label.showLog")}
          title={showEventLog ? t("gameHeader.label.hideLog") : t("gameHeader.label.showLog")}
        >
          <ScrollText className="w-4 h-4 text-[#1A237E]/70 dark:text-[#E3F2FD]/70" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden p-1.5"
          onClick={() => setShowDecks(!showDecks)}
          data-testid="toggle-decks-icon"
          aria-expanded={showDecks}
          aria-pressed={showDecks}
          aria-label={showDecks ? t("gameHeader.label.hideDecks") : t("gameHeader.label.showDecks")}
          title={showDecks ? t("gameHeader.label.hideDecks") : t("gameHeader.label.showDecks")}
        >
          <Layers className="w-4 h-4 text-[#1A237E]/70 dark:text-[#E3F2FD]/70" aria-hidden="true" />
        </Button>
        <MobileMenu>
          {canPause && onTogglePause && (
            <Button
              variant="ghost"
              size="sm"
              className="p-1 sm:p-1 text-[#1A237E] dark:text-[#E3F2FD] hover:bg-[#38B6FF]/10 justify-start sm:justify-center"
              onClick={onTogglePause}
              data-testid="pause-btn"
              title={game.paused ? t("gameHeader.label.resume") : t("gameHeader.label.pauseTitle")}
              aria-label={game.paused ? t("gameHeader.label.resume") : t("gameHeader.label.pause")}
              aria-pressed={!!game.paused}
            >
              {game.paused ? (
                <Play className="w-4 h-4 mr-2 sm:mr-0" aria-hidden="true" />
              ) : (
                <Pause className="w-4 h-4 mr-2 sm:mr-0" aria-hidden="true" />
              )}
              <span className="sm:hidden">
                {game.paused ? t("gameHeader.label.resume") : t("gameHeader.label.pause")}
              </span>
            </Button>
          )}
          {onSurrender && (
            <Button
              variant="ghost"
              size="sm"
              className="p-1 sm:p-1 text-[#FF5A5F] hover:bg-[#FF5A5F] hover:text-white dark:hover:text-white transition-colors justify-start sm:justify-center"
              onClick={onSurrender}
              data-testid="surrender-btn"
              title={t("gameHeader.label.surrender")}
              aria-label={t("gameHeader.label.surrender")}
            >
              <Flag className="w-4 h-4 mr-2 sm:mr-0" aria-hidden="true" />
              <span className="sm:hidden">{t("gameHeader.label.surrender")}</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="p-1 sm:p-1 justify-start sm:justify-center"
            onClick={() => setMuted(!muted)}
            data-testid="sound-toggle-btn"
            aria-pressed={muted}
            aria-label={muted ? t("gameHeader.label.unmuteSounds") : t("gameHeader.label.muteSounds")}
          >
            {muted ? (
              <VolumeX className="w-4 h-4 mr-2 sm:mr-0 text-[#1A237E]/40 dark:text-[#E3F2FD]/40" aria-hidden="true" />
            ) : (
              <Volume2 className="w-4 h-4 mr-2 sm:mr-0 text-[#38B6FF]" aria-hidden="true" />
            )}
            <span className="sm:hidden">
              {muted ? t("gameHeader.label.unmuteSounds") : t("gameHeader.label.muteSounds")}
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="p-1 sm:p-1 justify-start sm:justify-center"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            data-testid="theme-toggle-btn"
            title={isDark ? t("gameHeader.label.switchLight") : t("gameHeader.label.switchDark")}
            aria-label={isDark ? t("gameHeader.label.switchLight") : t("gameHeader.label.switchDark")}
          >
            {isDark ? (
              <Sun className="w-4 h-4 mr-2 sm:mr-0 text-[#FFC107]" aria-hidden="true" />
            ) : (
              <Moon className="w-4 h-4 mr-2 sm:mr-0 text-[#1A237E]/60 dark:text-[#E3F2FD]/60" aria-hidden="true" />
            )}
            <span className="sm:hidden">
              {isDark ? t("gameHeader.label.switchLight") : t("gameHeader.label.switchDark")}
            </span>
          </Button>
        </MobileMenu>
      </div>
    </header>
  );
}

export default memo(GameHeader);


/**
 * Phase 3a — turn-clock countdown badge.
 *
 * Hidden when `turn_expires_at` isn't set (pass-and-play games, or
 * legacy games created before Phase 1 lands). Visual urgency:
 *   • 30s+ remaining → blue (matches phase badge tone)
 *   • 10–30s        → amber
 *   • ≤10s          → red + pulsing
 *
 * The 1Hz tick stops automatically on expiry (the underlying
 * `useCountdown` clears its interval at 0), so this component is cheap
 * even on long-stalled turns.
 */
function TurnClockChip({ turnExpiresAt, paused }) {
  const { t } = useTranslation();
  const seconds = useCountdown(turnExpiresAt);
  if (paused) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-[#1A237E]/30 dark:border-[#E3F2FD]/30 bg-[#1A237E]/5 dark:bg-[#E3F2FD]/5 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#1A237E] dark:text-[#E3F2FD]"
        data-testid="turn-clock-paused"
      >
        {t("gameHeader.badge.paused")}
      </span>
    );
  }
  if (!turnExpiresAt) return null;
  const tone =
    seconds <= 10
      ? "border-red-500 text-red-600 animate-pulse"
      : seconds <= 30
      ? "border-amber-500 text-amber-700"
      : "border-[#1A237E]/30 dark:border-[#E3F2FD]/30 text-[#1A237E] dark:text-[#E3F2FD]";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] sm:text-xs ${tone}`}
      data-testid="turn-clock-chip"
    >
      <Clock className="w-3 h-3 mr-0.5" /> {seconds}s
    </Badge>
  );
}
